/* eslint-disable jsx-a11y/media-has-caption */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import ReactPlayer from 'react-player';
import classNames from 'classnames';
import { BASE_URL, IMAGE_PATHS, VIDEO_PATHS } from '../../../../constants/paths.constants';
import { borderDarkGrey, transparentYellowBox, universalStyles, white, yellow } from '../../../../app/theme';
import CaseStudy from '../../../micro/caseStudy/CaseStudy';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '100%',
  },
  whiteBox: {
    backgroundColor: white,
    padding: '60px 51px',
  },
  column: {
    width: '48%',
  },
  measurementRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  headerRow: {
    marginTop: 50,
    width: 612,
    overflowX: 'visible',
    borderBottom: `1px solid ${borderDarkGrey}`,
    paddingBottom: 21,
    marginBottom: 20,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    margin: '30px 0 0',
  },
  title: {
    margin: 0,
    display: 'inline',
    width: '50vw',
    minWidth: 612,
  },
  subtitle: {
    margin: 0,
    fontWeight: 'normal',
    fontStyle: 'italic',
  },
  text: {
    margin: '0 0 25px',
  },
  yellowBox: {
    margin: '0 25px 33px -25px',
    width: '100%',
    padding: '21px 25px',
    backgroundColor: transparentYellowBox,
    marginTop: 20,
  },
  rowWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  columnWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
  },
  row: {
    width: '100%',
    marginBottom: 30,
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
  },
  videoBox: {
    maxHeight: 527,
    border: `6px solid ${yellow}`,
    textAlign: 'center',
  },
  video: {
    maxHeight: 435,
    aspectRatio: '.48',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'block',
  },
  rowTitle: {
    fontWeight: 'bold',
    marginBottom: 20,
  },
  boxRowTitle: {
    fontWeight: 'bold',
    marginBottom: 10,
    marginTop: 10,
  },
  spacer: {
    marginBottom: 50,
  },
}));

type InterpretationPage16Props = {
  setNextDisabled: (disabled: boolean) => void;
  setNextDisabledCS: (disabled: boolean) => void;
};

const InterpretationPage16 = ({ setNextDisabled, setNextDisabledCS }: InterpretationPage16Props): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_3.path}`}
          alt={`${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_3.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 3:</Typography>
          <Typography variant="h3">Assess and Evaluate Axumin Uptake in Various Settings (continued)</Typography>
          <Typography variant="h5" className={classes.header}>
            Bone
          </Typography>
          <div className={classes.column}>
            <ul className={classes.list}>
              <li>Focal uptake clearly visualized on MIP or PET-only images can be considered suspicious for cancer</li>
            </ul>
          </div>
          <div className={classes.columnWrapper}>
            <div className={classes.column}>
              <div className={classes.videoBox}>
                <div className={classes.rowWrapper}>
                  <Typography variant="body1" className={classes.boxRowTitle}>
                    Bone L3 Focal Uptake
                  </Typography>
                  <div className={classes.row}>
                    <div className={classes.video}>
                      <ReactPlayer className={classes.responsiveVideo} url={VIDEO_PATHS.FOCAL_UPTAKE.path} controls />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.column}>
              <ul className={classes.list}>
                <li>
                  <span>
                    A bone abnormality visualized on CT (eg, sclerosis without uptake) may still represent a metastasis.
                    Alternative imaging, for example MRI,
                    <sup>18</sup>
                    F-NaF PET,
                    <sup>99m</sup>
                    Tc SPECT/CT bone scan or standard bone bone scintigraphy, should be considered
                  </span>
                </li>
                <li>
                  Due to normal physiologic heterogeneity of bone marrow, appropriate PET display windowing must be used
                </li>
                <li>
                  Increased uptake in bone may be seen in the setting of trauma (including compression fractures) or
                  occasionally degenerative changes
                </li>
                <li>
                  Skeletal metastases, which resemble Schmorl’s nodes but with uptake within them, have been described
                </li>
              </ul>
            </div>
          </div>
          <div className={classes.headerRow}>
            <Typography variant="h5" className={classes.title}>
              Please review the images and findings below
            </Typography>
            <Typography variant="h5" className={classNames(classes.title, classes.subtitle)}>
              See arrows for the location of the lesions and review findings
            </Typography>
          </div>
          <Typography variant="body1" className={classes.text}>
            <em>Subject presented with rising PSA (9.5 ng/mL), post-prostatectomy and salvage radiotherapy</em>
          </Typography>
          <div className={classNames(classes.yellowBox, classes.rowWrapper)}>
            <div className={classes.row}>
              <Typography variant="body1" className={classes.rowTitle}>
                Cranial to caudal view
              </Typography>
              <img
                className={classes.image}
                src={`${BASE_URL}${IMAGE_PATHS.CRANIAL_CAUDAL_VIEW.path}`}
                alt={IMAGE_PATHS.CRANIAL_CAUDAL_VIEW.alt}
              />
            </div>
            <div className={classes.row}>
              <img
                className={classes.image}
                src={`${BASE_URL}${IMAGE_PATHS.CRANIAL_CAUDAL_VIEW_2.path}`}
                alt={IMAGE_PATHS.CRANIAL_CAUDAL_VIEW_2.alt}
              />
            </div>
          </div>
          <Typography variant="body1" className={classes.rowTitle}>
            Findings:
          </Typography>
          <ul className={classes.list}>
            <li>
              Image interpretation: multiple foci positive (including sub-cm retroperitoneal nodes and sternum) (yellow
              arrows). Sternum biopsy positive prostate cancer
            </li>
          </ul>
          <div className={classes.headerRow}>
            <Typography variant="h5" className={classes.title}>
              Please review the images and findings below
            </Typography>
            <Typography variant="h5" className={classNames(classes.title, classes.subtitle)}>
              See arrows for the location of the lesions and review findings
            </Typography>
          </div>
          <div className={classNames(classes.yellowBox, classes.rowWrapper)}>
            <div className={classes.row}>
              <Typography variant="body1" className={classes.rowTitle}>
                Cranial to caudal view
              </Typography>
              <img
                className={classes.image}
                src={`${BASE_URL}${IMAGE_PATHS.CRANIAL_CAUDAL_VIEW_3.path}`}
                alt={IMAGE_PATHS.CRANIAL_CAUDAL_VIEW_3.alt}
              />
            </div>
          </div>
          <Typography variant="body1" className={classes.rowTitle}>
            Findings:
          </Typography>
          <ul className={classes.list}>
            <li>
              Dense sclerotic lesion, without uptake; cannot exclude metastases (yellow arrow). Recommend follow-up
              imaging such as MR or bone scan
            </li>
          </ul>
          <div className={classNames(classes.yellowBox, classes.rowWrapper)}>
            <div className={classes.row}>
              <Typography variant="body1" className={classes.rowTitle}>
                Axial view
              </Typography>
              <img
                className={classes.image}
                src={`${BASE_URL}${IMAGE_PATHS.AXIAL_VIEW_1.path}`}
                alt={IMAGE_PATHS.AXIAL_VIEW_1.alt}
              />
            </div>
          </div>
          <Typography variant="body1" className={classes.rowTitle}>
            Findings:
          </Typography>
          <ul className={classNames(classes.list, classes.spacer)}>
            <li>
              Mildly sclerotic lesion without Axumin uptake (yellow arrow), so considered negative despite positive MR.
              Biopsy negative
            </li>
          </ul>
          <CaseStudy trainingId={1} module="axuminInterpretation" page={16} setNextDisabledCS={setNextDisabledCS} />
        </div>
      </div>
      <LearningCheckQuestions
        module="axuminInterpretation"
        page={16}
        setNextDisabled={setNextDisabled}
        trainingId={1}
      />
    </>
  );
};

export default InterpretationPage16;
